// Per-conversation composer drafts.
//
// The chat composer is one piece of state shared by every conversation, so
// without somewhere to park it the text typed in one chat follows you into
// the next one you open. These helpers own the two rules that decide what
// gets parked and what comes back:
//
//   • an emptied composer clears the draft rather than pinning a blank one
//   • text being *edited* is an existing message body, not a new message, so
//     it is dropped together with the edit when the conversation is left
//
// Persisting them across restarts is a separate concern — see
// utils/chat/draftStorage.ts, which serialises whatever store it is handed.

import { setBoundedMapEntry } from '../../utils/boundedMap';

export type DraftStore = Map<string, string>;

/**
 * How many conversations may hold a draft at once. Drafts are kept forever
 * otherwise, so without a ceiling the store would grow for the life of the
 * login. Insertion order decides: writing a draft makes it the newest, and
 * the conversation untouched for longest is the one dropped.
 */
export const MAX_DRAFT_CONVERSATIONS = 200;

export const createDraftStore = (): DraftStore => new Map<string, string>();

/**
 * Park the composer text of the conversation being left.
 *
 * Whitespace-only text is dropped so a stray space can't resurrect an
 * apparently empty draft, but anything kept is stored verbatim — the spacing
 * inside a half-written message is part of what the user typed.
 */
export const stashDraft = (
  store: DraftStore,
  convId: string | null | undefined,
  text: string,
  opts: { editing?: boolean } = {},
): void => {
  if (!convId) return;
  if (opts.editing || !text.trim()) {
    store.delete(convId);
    return;
  }
  setBoundedMapEntry(store, convId, text, MAX_DRAFT_CONVERSATIONS);
};

/** The text to put back in the composer when a conversation is opened. */
export const readDraft = (
  store: DraftStore,
  convId: string | null | undefined,
): string => (convId ? store.get(convId) ?? '' : '');
